import Markdown from 'markdown-to-jsx';
import { CSSProperties, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { NewProfile } from '../../ifaces/profile';
import { useProgramProgressFeature } from '../hooks/use-progress-tracker';
import { usePromoContext } from '../hooks/use-promo-context';
import { ExerciseHelp, ExerciseHelpButton } from './ExerciseHelp';
import { PromoDialog } from './PromoDialog';
import { AvatarPointer, TutorAvatar } from './TutorAvatar';
import { Text } from './Typography';

// @ts-expect-error Don't care why
const HAS_PROMO_CONFIG = !!import.meta.env.VITE_PROMO_LINK;

export function ModalLayout({
  maxWidth = 560,
  onBack,
  hideHeader,
  children,
}: PropsWithChildren<{
  maxWidth?: number;
  hideHeader?: boolean;
  onBack?: () => void;
}>) {
  return (
    <div
      className="tutor-panels-single"
      style={{
        boxShadow: 'inset 0px 1px 1px rgba(90, 90, 90, 0.1)',
        minHeight: 'calc(100vh - 200px)',
        backgroundImage: `linear-gradient(to bottom, var(--colors-cream) 80%, var(--colors-white))`,
        paddingTop: 100,
      }}
    >
      <div
        style={{
          maxWidth,
          margin: 'auto',
          backgroundColor: 'var(--colors-white)',
          padding: '40px 40px',
          borderRadius: 10,
          boxShadow: '0px 4px 16px rgba(100, 100, 100, 0.03)',
        }}
      >
        {onBack && (
          <div
            style={{ height: 0, overflow: 'visible', position: 'relative', left: -20, top: -25 }}
          >
            <button className="ghost" onClick={onBack}>
              <svg
                width="18"
                height="13"
                viewBox="0 0 18 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.61366 0.21967C6.90656 0.512563 6.90656 0.987437 6.61366 1.28033L1.81066 6.08333L6.61366 10.8863C6.90656 11.1792 6.90656 11.6541 6.61366 11.947C6.32077 12.2399 5.8459 12.2399 5.553 11.947L0.21967 6.61366C-0.0732233 6.32077 -0.0732233 5.8459 0.21967 5.553L5.553 0.21967C5.8459 -0.0732233 6.32077 -0.0732233 6.61366 0.21967Z"
                  fill="#22242F"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.355469 6.08341C0.355469 5.6692 0.691255 5.33341 1.10547 5.33341H16.7499C17.1641 5.33341 17.4999 5.6692 17.4999 6.08341C17.4999 6.49762 17.1641 6.83341 16.7499 6.83341H1.10547C0.691255 6.83341 0.355469 6.49762 0.355469 6.08341Z"
                  fill="#22242F"
                />
              </svg>
            </button>
          </div>
        )}
        {!hideHeader && (
          <div className="flex justify-center items-center gap-3" style={{ marginBottom: 40 }}>
            <TutorAvatar />
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export function TutorLayout({
  maxWidth = 700,
  style,
  header,
  children,
}: PropsWithChildren<{
  maxWidth?: number;
  header?: ReactNode;
  style?: CSSProperties;
}>) {
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const { promoKey } = usePromoContext();
  const { enabled: showingProgramProgress } = useProgramProgressFeature();

  useEffect(() => {
    if (!promoKey) return;
    // Check if this promo has already been shown/dismissed
    const dismissedPromos = JSON.parse(localStorage.getItem('dismissedPromos') || '[]');
    if (dismissedPromos.includes(promoKey)) return;
    setShowPromoDialog(true);
  }, [promoKey]);

  const handlePromoClose = () => {
    // Track that this promo has been dismissed
    const dismissedPromos = JSON.parse(localStorage.getItem('dismissedPromos') || '[]');
    if (!dismissedPromos.includes(promoKey)) {
      dismissedPromos.push(promoKey);
      localStorage.setItem('dismissedPromos', JSON.stringify(dismissedPromos));
    }
    setShowPromoDialog(false);
  };

  return (
    <>
      {HAS_PROMO_CONFIG && promoKey && showPromoDialog && (
        <PromoDialog
          open
          promoKey={promoKey}
          onCancel={() => handlePromoClose()}
          onSuccess={() => handlePromoClose()}
        />
      )}
      <div className="flex justify-center" style={{ flex: 1, minHeight: '100%' }}>
        <div
          className={`w-full my-0 px-5 ${showingProgramProgress ? 'pt-35 sm:pt-40' : 'pt-20'}`}
          style={{ ...style }}
        >
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ flex: 1, maxWidth, minWidth: 420, paddingBottom: 40 }}>
              <div className="flex justify-between items-center gap-3">
                <TutorAvatar />
                {header}
              </div>
              {children}
            </div>
            <div id="tutor-sider" />
          </div>
        </div>
      </div>
    </>
  );
}

export function TutorWorkPanel(
  props: PropsWithChildren<{
    label?: string;
    style?: CSSProperties;
    meta?: ReactNode;
  }>,
) {
  return (
    <div className="tutor-work-panel" style={props.style}>
      {props.label && <label>{props.label}</label>}
      {props.meta && <label className="meta">{props.meta}</label>}
      <Text header semiBold m>
        {props.children}
      </Text>
    </div>
  );
}

export function TutorPrimaryPanel(
  props: PropsWithChildren<{
    pointer?: boolean;
    hint?: string | true;
    unanimated?: boolean;
    onRegenerate?: (reason: string) => Promise<void>;
    onProfileChange?: (profile: NewProfile) => Promise<void>;
    session_id?: string;
    profile?: NewProfile;
    skill_slug?: string;
    competency_slug?: string;
  }>,
) {
  const [showingHelp, showHelp] = useState(false);
  const [isSmall, setIsSmall] = useState(false);

  useEffect(() => {
    const checkSmall = () => {
      setIsSmall(window.innerWidth < (showingHelp ? 1000 : 640));
    };

    checkSmall();
    window.addEventListener('resize', checkSmall);
    return () => window.removeEventListener('resize', checkSmall);
  }, [showingHelp]);
  return (
    <>
      <div className={`tutor-panel-message ${props.unanimated ? ' unanimated' : ''}`}>
        {props.hint !== undefined && !showingHelp && (
          <div style={{ position: 'relative' }}>
            <div
              style={{
                height: 1,
                overflow: 'visible',
                position: 'relative',
                top: isSmall ? -67 : -112, // scoot under Beta tag when needed
                right: -17,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <ExerciseHelpButton
                isOpen={showingHelp}
                onClick={() => {
                  if (showingHelp) showHelp(false);
                  else showHelp(true);
                }}
              />
            </div>
          </div>
        )}
        <Text header s semiBold warm50>
          {props.children}
        </Text>
        {props.pointer && <AvatarPointer />}
      </div>
      {showingHelp && (
        <ExerciseHelp
          hint={props.hint}
          isOpen={showingHelp}
          onClose={() => showHelp(false)}
          profile={props.profile}
          onProfileChange={props.onProfileChange}
          onRegenerate={props.onRegenerate}
          program_slug={'basic-ai-proficiency'}
          skill_slug={props.skill_slug}
          competency_slug={props.competency_slug}
          session_id={props.session_id}
        />
      )}
    </>
  );
}

export function TutorInstructionsPanel(props: PropsWithChildren<{ style?: CSSProperties }>) {
  return (
    <div style={{ padding: '30px 10px', userSelect: 'none', ...props.style }}>
      <Text xl semiBold warm50>
        {props.children}
      </Text>
    </div>
  );
}

export function TutorFeedbackPanel({
  feedback,
  passed,
}: {
  feedback: string | ReactNode;
  passed: boolean;
}) {
  const [hidden, hide] = useState(false);
  useEffect(() => hide(false), [feedback]);
  if (hidden) return null;
  return (
    <div
      key={typeof feedback === 'string' ? feedback : passed ? 'success' : 'error'}
      className={`tutor-panel-${passed ? 'success' : 'error'}`}
      style={{ marginBottom: 20 }}
    >
      <div style={{ height: 0, width: '100%', overflow: 'visible', position: 'relative' }}>
        <button
          style={{ position: 'absolute', top: -16, right: -14 }}
          className="tutor-panel-close-button"
          onClick={() => hide(true)}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11 1L1 11M1 1L11 11"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <div className="flex items-start gap-5">
        <div style={{ minWidth: 45, borderRadius: '50%', overflow: 'hidden' }}>
          <TutorAvatar noText size={45} />
        </div>
        {typeof feedback === 'string' ? (
          <Text xl>
            <Markdown>{feedback}</Markdown>
          </Text>
        ) : (
          feedback
        )}
      </div>
    </div>
  );
}

export function TutorSecondaryPanel(props: PropsWithChildren<{ unanimated?: boolean }>) {
  return (
    <div className={`tutor-panel-secondary ${props.unanimated ? ' unanimated' : ''}`}>
      {props.children}
    </div>
  );
}

export function TutorSpacingPanel(props: PropsWithChildren<{ style?: CSSProperties }>) {
  return (
    <div className="tutor-panel-spacing" style={props.style}>
      {props.children}
    </div>
  );
}
